// Turns the synopsis JSON stored for a chapter into one HTML document for the admin preview.
// Text fields are HTML strings (sometimes entity-escaped, as the source XML held the markup as
// text); the document is shown in a sandboxed iframe, so the markup can't run scripts.

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];

const text = (v) => (typeof v === "string" ? v.trim() : "");

export const decodeEscapedHtml = (html) => {
  const value = String(html || "");
  if (!/&lt;\/?[a-z][a-z0-9]*(\s[^<>]*?)?\/?&gt;/i.test(value)) return value;
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, "&");
};

// A paragraph holding only whitespace / non-breaking spaces: authors pad with these, and they only add gaps.
const EMPTY_PARAGRAPH = /<p(?:\s[^>]*)?>(?:[\s ]|&nbsp;|<span[^>]*>(?:[\s ]|&nbsp;)*<\/span>)*<\/p>/gi;

const html = (v) => decodeEscapedHtml(text(v)).replace(EMPTY_PARAGRAPH, "");
const plain = (v) => html(v).replace(/<[^>]*>/g, "").trim();

// correctAnswer may be "1".."4", "A".."D", or the option text itself.
export const correctOptionIndex = (options, correctAnswer) => {
  const answer = plain(correctAnswer);
  if (!answer) return -1;
  let index = -1;
  if (/^[1-9]$/.test(answer)) index = Number(answer) - 1;
  else if (/^[A-Fa-f]$/.test(answer)) index = OPTION_LETTERS.indexOf(answer.toUpperCase());
  else return options.findIndex((o) => plain(o).toLowerCase() === answer.toLowerCase());
  return index < options.length ? index : -1;
};

export const isSynopsisEmpty = (data) =>
  !data || (!(data.topics || []).length && !text(data.mindmap) && !text(data.weightage));

// Option text authored as "A. text" / "(b) text" already carries its letter.
const hasOwnLetter = (o) => /^\s*\(?[A-Da-d1-4][.)]\s/.test(plain(o));

const escapeText = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const section = (title, body) => (body ? `<section><h2>${title}</h2>${body}</section>` : "");

const solvedQuestion = (q, n) => {
  const options = (q?.options || []).map(text).filter(Boolean);
  const correct = correctOptionIndex(options, q?.correctAnswer);
  const optionRows = options
    .map(
      (o, i) =>
        `<li class="opt${i === correct ? " correct" : ""}">${hasOwnLetter(o) ? "" : `<b>${OPTION_LETTERS[i] || i + 1}</b>`}<div>${html(o)}</div></li>`,
    )
    .join("");
  const answer = correct >= 0 ? html(options[correct]) : html(q?.correctAnswer);
  const explanation = html(q?.explanation);
  // Collapsed like the mobile app: "Question n" with a one-line preview (minus the authored "Q1." marker).
  const preview = plain(q?.question).replace(/^(?:question|q)\s*(?:\d+\s*[.):-]*|[.):-]+)\s*/i, "");
  return `<details class="q"><summary class="qhead"><span class="qlabel">Question ${n}</span><span class="qpreview">${escapeText(preview)}</span></summary>
    <div class="qtext">${html(q?.question)}</div>
    <ul class="opts">${optionRows}</ul>
    <details class="ans"><summary><span class="show">Show Answer</span><span class="hide">Hide Answer</span></summary>
      ${answer ? `<div class="alabel">Answer${correct >= 0 ? ` · ${OPTION_LETTERS[correct] || correct + 1}` : ""}:</div>${answer}` : ""}
      ${explanation ? `<div class="alabel">Explanation:</div>${explanation}` : ""}
    </details></details>`;
};

export const buildSynopsisHtml = (data) => {
  const topics = (data?.topics || [])
    .map((topic, ti) => {
      const subs = (topic?.periods || [])
        .flatMap((p) => p?.subtopics || [])
        .map((sub) => {
          const solved = sub?.solvedQuestions || [];
          const questions = solved.length
            ? `<h4 class="test">🎯 Test Your Understanding</h4>${solved.map((q, qi) => solvedQuestion(q, qi + 1)).join("")}`
            : "";
          return `<div class="sub"><h3>${html(sub?.heading) || "Overview"}</h3>${html(sub?.content)}${questions}</div>`;
        })
        .join("");
      return `<section><h2>${html(topic?.heading) || `Topic ${ti + 1}`}</h2>${subs}</section>`;
    })
    .join("");
  const body = [section("Weightage", html(data?.weightage)), section("Mind map", html(data?.mindmap)), topics].join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font:14px/1.6 Arial,Helvetica,sans-serif;color:#222;margin:0;padding:16px;background:#f7f8fb}
    section{background:#fff;border-radius:10px;padding:14px 16px;margin-bottom:14px;box-shadow:0 1px 3px rgba(0,0,0,.08)}
    h2{font-size:16px;color:#17479e;margin:0 0 8px} h3{font-size:14px;margin:0 0 6px}
    .sub{border-top:1px solid #e0e4ee;padding-top:10px;margin-top:10px}
    img{max-width:100%;height:auto} table{border-collapse:collapse;max-width:100%}
    td,th{border:1px solid #cfd6e4;padding:4px 8px}
    /* Only the heading row is shaded (as in the mobile app). Word's banded table styles arrive as inline
       backgrounds on body rows, so body cells are reset and the heading row (th, thead, or the first row
       of a table without a thead) is shaded on top. */
    td,td *{background-color:transparent !important}
    th,thead td,table:not(:has(thead))>tbody:first-of-type>tr:first-child>td{background-color:#f0f2f5 !important;font-weight:700}
    .q{background:#eef1fb;border-radius:8px;padding:10px 12px;margin-top:10px}
    h4.test{font-size:14px;color:#17479e;margin:16px 0 0}
    details.q>summary.qhead{display:flex;align-items:baseline;gap:8px;list-style:none;cursor:pointer}
    details.q>summary.qhead::-webkit-details-marker{display:none}
    details.q>summary.qhead::after{content:"\\25BE";margin-left:auto;color:#5b6b82}
    details.q[open]>summary.qhead::after{content:"\\25B4"}
    .qpreview{flex:1;min-width:0;color:#5b6b82;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    details.q[open]>summary .qpreview{display:none}
    details.ans{margin-top:8px} details.ans>summary .hide,details.ans[open]>summary .show{display:none}
    details.ans[open]>summary .hide{display:inline}
    .qlabel{font-size:12px;font-weight:700;color:#17479e} .qtext{font-weight:600;margin:4px 0}
    .opts{list-style:none;padding:0;margin:6px 0}
    .opt{display:flex;gap:8px;border:1px solid #e0e4ee;background:#fff;border-radius:8px;padding:4px 10px;margin-top:6px}
    .opt.correct{border-color:#2e7d32;background:#eaf7ec} .opt div{flex:1} .opt p,.qtext p{margin:2px 0}
    summary{cursor:pointer;color:#17479e;font-weight:700;font-size:13px}
    .alabel{font-size:13px;font-weight:700;color:#17479e;margin-top:8px}
  </style></head><body>${body}</body></html>`;
};
